/**
 * features/onboarding/schemas — Zod schemas mirroring the API contract.
 *
 * Single source of truth for client-side wizard validation. These MUST stay
 * bit-compatible with the server Zod schemas (ADR-108 §2 API Contract). The
 * backend schemas live in `backend/src/domains/business/onboarding/schemas.ts`
 * (not yet built at time of writing); CI gate `business-onboarding-zod-schema`
 * will diff these two files once both exist.
 *
 * @see ADR-108 §2 API Contract
 */
import { z } from 'zod';
import { findSubVertical } from './sub-verticals';

const SUPPORTED_LOCALES = ['tr', 'en'] as const;

/**
 * TR VKN checksum (Maliye algoritması).
 * 10 digits; algorithm per TC Gelir İdaresi Başkanlığı spec.
 */
function isValidTrTaxId(value: string): boolean {
  if (!/^\d{10}$/.test(value)) return false;
  const digits = value.split('').map(Number);
  const last = digits[9];
  let total = 0;
  for (let i = 0; i < 9; i += 1) {
    const d = digits[i];
    const tmp = (d + (9 - i)) % 10;
    total += tmp === 0 ? tmp : (tmp * 2 ** (9 - i)) % 9 || 9;
  }
  return (10 - (total % 10)) % 10 === last;
}

const emailSchema = z
  .string()
  .trim()
  .min(1)
  .max(254)
  .email()
  .transform(s => s.toLowerCase());

const passwordSchema = z
  .string()
  .min(12, 'min_length')
  .max(128, 'max_length')
  .refine(v => /[A-Za-z]/.test(v), { message: 'requires_letter' })
  .refine(v => /\d/.test(v), { message: 'requires_digit' });

const displayNameSchema = z
  .string()
  .trim()
  .min(2, 'min_length')
  .max(80, 'max_length');

const localeSchema = z.enum(SUPPORTED_LOCALES);

export const accountStepSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: displayNameSchema,
  locale: localeSchema,
  acceptTerms: z.literal(true, {
    errorMap: () => ({ message: 'terms_not_accepted' }),
  }),
  marketingOptIn: z.boolean().optional(),
});
export type AccountStepValues = z.infer<typeof accountStepSchema>;

export const businessStepSchema = z.object({
  businessName: z.string().trim().min(2).max(200),
  taxId: z.string().trim().regex(/^\d{10}$/, 'invalid_format').refine(
    v => isValidTrTaxId(v),
    { message: 'invalid_checksum' },
  ),
  countryCode: z.string().trim().length(2).default('TR'),
  subVerticalKey: z.string().trim().min(1).refine(
    key => Boolean(findSubVertical(key)),
    { message: 'sub_vertical_not_found' },
  ),
});
export type BusinessStepValues = z.infer<typeof businessStepSchema>;

const operatingHoursEntrySchema = z.object({
  weekday: z.union([
    z.literal(1), z.literal(2), z.literal(3), z.literal(4),
    z.literal(5), z.literal(6), z.literal(7),
  ]),
  openTime: z.string().regex(/^\d{2}:\d{2}$/, 'invalid_time').nullable(),
  closeTime: z.string().regex(/^\d{2}:\d{2}$/, 'invalid_time').nullable(),
}).refine(
  (v) => (v.openTime === null) === (v.closeTime === null),
  { message: 'open_close_pair_required' },
);

const addressSchema = z.object({
  street: z.string().trim().min(2).max(200),
  city: z.string().trim().min(2).max(100),
  district: z.string().trim().min(2).max(100),
  postalCode: z.string().trim().regex(/^\d{5}$/, 'invalid_postal_code'),
  countryCode: z.string().trim().length(2).default('TR'),
});

const e164Phone = z
  .string()
  .trim()
  .regex(/^\+\d{8,15}$/, 'invalid_phone');

export const branchStepSchema = z.object({
  branchName: z.string().trim().min(2).max(200),
  address: addressSchema,
  timezone: z.string().trim().min(3).default('Europe/Istanbul'),
  phone: e164Phone,
  operatingHours: z.array(operatingHoursEntrySchema).optional(),
});
export type BranchStepValues = z.infer<typeof branchStepSchema>;

export const provisionRequestSchema = z.object({
  account: accountStepSchema,
  business: businessStepSchema,
  branch: branchStepSchema,
});
export type ProvisionRequestValues = z.infer<typeof provisionRequestSchema>;

/** Reused by the Lovable wizard primitives to know when "Next" can enable. */
export function validateStep(
  step: 1 | 2 | 3,
  slice: unknown,
): { ok: true } | { ok: false; errors: { path: string; message: string }[] } {
  const schema =
    step === 1 ? accountStepSchema : step === 2 ? businessStepSchema : branchStepSchema;
  const result = schema.safeParse(slice);
  if (result.success) return { ok: true };
  return {
    ok: false,
    errors: result.error.issues.map((i) => ({
      path: i.path.join('.'),
      message: i.message,
    })),
  };
}
